/**
 * Toast notification utility for user feedback.
 * Provides a simple API for showing success, error, warning, and info messages.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastOptions {
  duration?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

interface ToastItem {
  id: string
  message: string
  type: ToastType
  duration: number
}

// Toast container element ID
const TOAST_CONTAINER_ID = 'tutara-toast-container'

// Default durations by type
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
}

// Toast styles by type
const TOAST_STYLES: Record<ToastType, { bg: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    text: 'text-green-800 dark:text-green-300',
    icon: '✓',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-300',
    icon: '✕',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-800 dark:text-yellow-300',
    icon: '⚠',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    text: 'text-blue-800 dark:text-blue-300',
    icon: 'ℹ',
  },
}

/**
 * Get or create the toast container element.
 */
function getOrCreateContainer(): HTMLElement {
  let container = document.getElementById(TOAST_CONTAINER_ID)

  if (!container) {
    container = document.createElement('div')
    container.id = TOAST_CONTAINER_ID
    container.className = 'fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm'
    container.setAttribute('aria-live', 'polite')
    container.setAttribute('aria-label', 'Notifications')
    document.body.appendChild(container)
  }

  return container
}

/**
 * Create a toast element.
 */
function createToastElement(toast: ToastItem): HTMLElement {
  const style = TOAST_STYLES[toast.type]

  const element = document.createElement('div')
  element.id = `toast-${toast.id}`
  element.className = `
    ${style.bg} ${style.text}
    border rounded-lg shadow-lg p-4
    flex items-start gap-3
    transform transition-all duration-300 ease-out
    translate-x-full opacity-0
  `.replace(/\s+/g, ' ').trim()
  element.setAttribute('role', 'alert')

  element.innerHTML = `
    <span class="text-lg flex-shrink-0">${style.icon}</span>
    <p class="text-sm flex-1">${escapeHtml(toast.message)}</p>
    <button
      class="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      aria-label="Dismiss notification"
    >
      ✕
    </button>
  `

  // Add close button handler
  const closeButton = element.querySelector('button')
  if (closeButton) {
    closeButton.addEventListener('click', () => removeToast(toast.id))
  }

  return element
}

/**
 * Escape HTML to prevent XSS in toast messages.
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Show a toast element with animation.
 */
function showToast(toast: ToastItem): void {
  const container = getOrCreateContainer()
  const element = createToastElement(toast)

  container.appendChild(element)

  // Trigger animation
  requestAnimationFrame(() => {
    element.classList.remove('translate-x-full', 'opacity-0')
  })

  // Auto-remove after duration
  setTimeout(() => {
    removeToast(toast.id)
  }, toast.duration)
}

/**
 * Remove a toast by ID.
 */
function removeToast(id: string): void {
  const element = document.getElementById(`toast-${id}`)
  if (!element) return

  // Animate out
  element.classList.add('translate-x-full', 'opacity-0')

  // Remove after animation
  setTimeout(() => {
    element.remove()
  }, 300)
}

/**
 * Generate a unique ID.
 */
function generateId(): string {
  return Math.random().toString(36).substring(2, 11)
}

/**
 * Show an actionable toast — message + a single inline button that fires
 * a callback when clicked. Used for affordances like "Undo" after an AI
 * mutation completes (Phase 4c).
 */
function showActionToast(opts: {
  message: string
  type: ToastType
  duration: number
  actionLabel: string
  onAction: () => void
}): void {
  const id = generateId()
  const style = TOAST_STYLES[opts.type]
  const container = getOrCreateContainer()

  const element = document.createElement('div')
  element.id = `toast-${id}`
  element.className = `
    ${style.bg} ${style.text}
    border rounded-lg shadow-lg p-4
    flex items-center gap-3
    transform transition-all duration-300 ease-out
    translate-x-full opacity-0
  `.replace(/\s+/g, ' ').trim()
  element.setAttribute('role', 'alert')

  const iconSpan = document.createElement('span')
  iconSpan.className = 'text-lg flex-shrink-0'
  iconSpan.textContent = style.icon

  const messageP = document.createElement('p')
  messageP.className = 'text-sm flex-1'
  messageP.textContent = opts.message

  const actionBtn = document.createElement('button')
  actionBtn.className =
    'flex-shrink-0 text-sm font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity'
  actionBtn.setAttribute('aria-label', opts.actionLabel)
  actionBtn.textContent = opts.actionLabel
  actionBtn.addEventListener('click', () => {
    try {
      opts.onAction()
    } finally {
      removeToast(id)
    }
  })

  const dismissBtn = document.createElement('button')
  dismissBtn.className = 'flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity'
  dismissBtn.setAttribute('aria-label', 'Dismiss notification')
  dismissBtn.textContent = '✕'
  dismissBtn.addEventListener('click', () => removeToast(id))

  element.appendChild(iconSpan)
  element.appendChild(messageP)
  element.appendChild(actionBtn)
  element.appendChild(dismissBtn)

  container.appendChild(element)
  requestAnimationFrame(() => {
    element.classList.remove('translate-x-full', 'opacity-0')
  })
  setTimeout(() => removeToast(id), opts.duration)
}

/**
 * Toast API
 */
export const toast = {
  /**
   * Show a success toast.
   */
  success(message: string, options?: ToastOptions): void {
    showToast({
      id: generateId(),
      message,
      type: 'success',
      duration: options?.duration ?? DEFAULT_DURATIONS.success,
    })
  },

  /**
   * Show an error toast.
   */
  error(message: string, options?: ToastOptions): void {
    showToast({
      id: generateId(),
      message,
      type: 'error',
      duration: options?.duration ?? DEFAULT_DURATIONS.error,
    })
  },

  /**
   * Show a warning toast.
   */
  warning(message: string, options?: ToastOptions): void {
    showToast({
      id: generateId(),
      message,
      type: 'warning',
      duration: options?.duration ?? DEFAULT_DURATIONS.warning,
    })
  },

  /**
   * Show an info toast.
   */
  info(message: string, options?: ToastOptions): void {
    showToast({
      id: generateId(),
      message,
      type: 'info',
      duration: options?.duration ?? DEFAULT_DURATIONS.info,
    })
  },

  /**
   * Show a toast with an inline action button. The toast auto-dismisses
   * after `duration` ms; the action also dismisses on click.
   *
   * @since Phase 4c — Undo affordance after AI mutations.
   */
  action(opts: {
    message: string
    actionLabel: string
    onAction: () => void
    type?: ToastType
    duration?: number
  }): void {
    const type = opts.type ?? 'success'
    showActionToast({
      message: opts.message,
      type,
      duration: opts.duration ?? 8000,
      actionLabel: opts.actionLabel,
      onAction: opts.onAction,
    })
  },
}

export default toast
